import { getAuthSessionRevision, getStoredAuthToken, subscribeAuthSession } from '@/lib/authStorage';
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useAuth } from "@/context/AuthContext";
import { siteApi } from "@/lib/api";
import config from "@/lib/config";

import { publicWalletSettings } from '@/features/usdtDeposit/helpers/walletPrivacy.helper';

const settingsCacheByScope = new Map();
const settingsRequestByScope = new Map();
const settingsScopeTokenByScope = new Map();
const SETTINGS_CACHE_TTL_MS = 60_000;
const SETTINGS_CACHE_MAX_SCOPES = 8;
let unresolvedScopeSequence = 0;

const getSettingsScopeKey = (user = {}, unresolvedScopeKey = "unresolved") => {
  if (!user?.isAuthenticated) return "guest";

  const profile = user?.profile || {};
  const userId =
    user?.id ||
    profile?.id ||
    profile?.user_id ||
    profile?.member_id ||
    user?.username ||
    profile?.user_unique_id ||
    profile?.username ||
    user?.email ||
    profile?.email ||
    unresolvedScopeKey;
  const signupType =
    typeof profile?.signup_type === "string" ? profile.signup_type.trim().toUpperCase() : "";

  return signupType ? `authenticated:${userId}:${signupType}` : `authenticated:${userId}`;
};

const getScopeToken = (scopeKey) => {
  if (!settingsScopeTokenByScope.has(scopeKey)) {
    settingsScopeTokenByScope.set(scopeKey, Symbol(scopeKey));
  }
  return settingsScopeTokenByScope.get(scopeKey);
};

const invalidateSettingsScope = (scopeKey) => {
  if (!scopeKey) return;
  settingsScopeTokenByScope.delete(scopeKey);
  settingsCacheByScope.delete(scopeKey);
  settingsRequestByScope.delete(scopeKey);
};

const readCachedSettings = (scopeKey) => {
  const entry = settingsCacheByScope.get(scopeKey);
  if (!entry) return null;
  if (Date.now() - entry.loadedAt >= SETTINGS_CACHE_TTL_MS) {
    invalidateSettingsScope(scopeKey);
    return null;
  }
  return entry.settings;
};

const cacheSettings = (scopeKey, settings) => {
  settingsCacheByScope.delete(scopeKey);
  settingsCacheByScope.set(scopeKey, { settings, loadedAt: Date.now() });

  while (settingsCacheByScope.size > SETTINGS_CACHE_MAX_SCOPES) {
    const oldestScopeKey = settingsCacheByScope.keys().next().value;
    invalidateSettingsScope(oldestScopeKey);
  }
};

const trimLeadingSlash = (value = "") => value.replace(/^\/+/, "");

export const toAbsoluteAssetUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  if (/^(https?:)?\/\//i.test(value) || value.startsWith("data:")) {
    return value;
  }
  return `${config.backendUrl}/${trimLeadingSlash(value)}`;
};

export const resolveSiteLogo = (settings = {}) =>
  toAbsoluteAssetUrl(
    settings.logo_image ||
      settings.logo ||
      settings.site_logo ||
      settings.domainSettings?.logo_image ||
      ""
  );

const sortBannerItems = (items = []) =>
  [...items].sort(
    (a, b) =>
      Number(a?.sequence ?? a?.order ?? a?.sort ?? 0) -
      Number(b?.sequence ?? b?.order ?? b?.sort ?? 0)
  );

const isUsableBannerItem = (item = {}) =>
  item?.is_use !== false &&
  item?.is_visible !== false &&
  item?.enabled !== false &&
  item?.status !== false;

const getBannerImageValue = (item = {}) =>
  item.upload_file ||
  item.pc_image ||
  item.image ||
  item.mobile_image ||
  item.banner_image ||
  item.file ||
  "";

const getBannerLinkValue = (item = {}) =>
  item.link || item.url || item.href || item.redirect_url || item.target_url || "";

const normalizeBannerItem = (item = {}, index = 0) => {
  const image = getBannerImageValue(item);
  if (!image) return null;

  return {
    id: String(item.id ?? item.sequence ?? item.order ?? `${image}-${index}`),
    image: toAbsoluteAssetUrl(image),
    link: getBannerLinkValue(item),
  };
};

const getBannerObject = (settings = {}) =>
  [
    settings.front_page_banner_settings_image,
    settings.main_banner_setting,
    settings.main_banner,
    settings.banner,
  ].find((item) => item && typeof item === "object" && !Array.isArray(item)) || {};

export const resolveSiteBannerItems = (settings = {}) => {
  const bannerObject = getBannerObject(settings);
  const collections = [
    settings.main_banner_settings,
    settings.front_page_banner_settings,
    settings.front_page_banner_settings_image,
    settings.main_banner_setting,
    settings.main_banners,
    settings.front_page_banners,
    settings.main_banner,
    settings.banner,
    bannerObject.items,
    bannerObject.rows,
    bannerObject.images,
  ].filter(Array.isArray);

  const bannerItems = collections.flatMap((items) => {
    const sortedItems = sortBannerItems(items);
    const activeItems = sortedItems.filter(
      (item) => isUsableBannerItem(item) && getBannerImageValue(item)
    );
    const usableItems = activeItems.length ? activeItems : sortedItems.filter(getBannerImageValue);
    return usableItems.map(normalizeBannerItem).filter(Boolean);
  });

  if (bannerItems.length) {
    const seen = new Set();
    return bannerItems.filter((item) => {
      const key = `${item.image}|${item.link}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  const singleBanner =
    normalizeBannerItem(bannerObject) ||
    normalizeBannerItem({
      image:
        settings.main_banner_image ||
        settings.main_banner_pc_image ||
        settings.front_page_banner_image ||
        settings.banner_image ||
        settings.site_banner ||
        "",
      link: settings.main_banner_link || settings.banner_link || "",
    });

  return singleBanner ? [singleBanner] : [];
};

export const resolveSiteBannerImage = (settings = {}) =>
  resolveSiteBannerItems(settings)[0]?.image || "";

export const resolveSiteBannerLink = (settings = {}) => {
  return resolveSiteBannerItems(settings)[0]?.link || "";
};

// Token/logout transitions invalidate pending private responses and cached finance data.
subscribeAuthSession(() => {
  for (const key of settingsScopeTokenByScope.keys()) if (key.startsWith('authenticated:')) invalidateSettingsScope(key);
});
const loadSettings = async (scopeKey) => {
  const revision = getAuthSessionRevision(), token = getStoredAuthToken();
  const current = () => revision === getAuthSessionRevision() && token === getStoredAuthToken();
  const cachedSettings = readCachedSettings(scopeKey);
  if (cachedSettings) return cachedSettings;

  if (!settingsRequestByScope.has(scopeKey)) {
    const requestToken = getScopeToken(scopeKey);
    const request = siteApi
      .getGlobalSettings(current)
      .then((result) => {
        if (!current()) return {};
        if (result?.success && result?.data) {
          const safeSettings = scopeKey.startsWith("authenticated:") ? result.data : publicWalletSettings(result.data);
          if (settingsScopeTokenByScope.get(scopeKey) === requestToken) {
            cacheSettings(scopeKey, safeSettings);
          }
          return safeSettings;
        }
        return {};
      })
      .catch(() => ({}))
      .finally(() => {
        if (settingsRequestByScope.get(scopeKey) === request) {
          settingsRequestByScope.delete(scopeKey);
          if (!settingsCacheByScope.has(scopeKey)) {
            settingsScopeTokenByScope.delete(scopeKey);
          }
        }
      });

    settingsRequestByScope.set(scopeKey, request);
  }

  return settingsRequestByScope.get(scopeKey);
};

const useSiteSettings = () => {
  const { user } = useAuth({ optional: true });
  const sessionRevision = useSyncExternalStore(subscribeAuthSession, getAuthSessionRevision, getAuthSessionRevision);
  const unresolvedScopeKeyRef = useRef(null);
  if (!unresolvedScopeKeyRef.current) {
    unresolvedScopeSequence += 1;
    unresolvedScopeKeyRef.current = `unresolved-${unresolvedScopeSequence}`;
  }
  const scopeKey = `${getSettingsScopeKey(user, unresolvedScopeKeyRef.current)}:session-${sessionRevision}`;
  const cachedSettingsValue = readCachedSettings(scopeKey);
  const cachedSettings = cachedSettingsValue || {};
  const hasCachedSettings = cachedSettingsValue !== null;
  const previousScopeKeyRef = useRef(scopeKey);
  const [state, setState] = useState(() => ({
    scopeKey,
    settings: cachedSettings,
    isLoading: !hasCachedSettings,
  }));

  useEffect(() => {
    let isCurrentScope = true;
    const revision = getAuthSessionRevision();
    const previousScopeKey = previousScopeKeyRef.current;
    if (previousScopeKey !== scopeKey && previousScopeKey.startsWith("authenticated:")) {
      invalidateSettingsScope(previousScopeKey);
    }
    previousScopeKeyRef.current = scopeKey;

    const scopeSettingsValue = readCachedSettings(scopeKey);
    const scopeSettings = scopeSettingsValue || {};
    const hasScopeCache = scopeSettingsValue !== null;

    setState({
      scopeKey,
      settings: scopeSettings,
      isLoading: !hasScopeCache,
    });

    if (hasScopeCache) {
      return () => {
        isCurrentScope = false;
      };
    }

    loadSettings(scopeKey)
      .then((loadedSettings) => {
        if (!isCurrentScope || revision !== getAuthSessionRevision()) return;
        setState({
          scopeKey,
          settings: loadedSettings,
          isLoading: false,
        });
      });

    return () => {
      isCurrentScope = false;
    };
  }, [scopeKey]);

  const isStateForCurrentScope = state.scopeKey === scopeKey;

  return {
    settings: isStateForCurrentScope ? state.settings : cachedSettings,
    isLoading: isStateForCurrentScope ? state.isLoading : !hasCachedSettings,
  };
};

export default useSiteSettings;
